import * as React from 'react';
import { styled } from '@mui/material/styles';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import GoDrawer from "../../../components/Inputs/GoDrawer";
import ConfigService from "./Config.service";
import Config from "./Config";
import EntityConstants from '../../../configs/EntityConstants';
import SpinService from "../../../services/SpinService";
import { get } from "../../../services/FetchHelper";

const Accordion = styled((props) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:not(:last-child)': {
        borderBottom: 0,
    },
    '&:before': {
        display: 'none',
    },
}));

const AccordionSummary = styled((props) => (
    <MuiAccordionSummary
        expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
        {...props}
    />
))(({ theme }) => ({
    backgroundColor:
        theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, .05)'
            : 'rgba(0, 0, 0, .03)',
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(90deg)',
    },
    '& .MuiAccordionSummary-content': {
        marginLeft: theme.spacing(1),
    },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

const colors = {
    group_header: "red",
    main_group: "green",
    sub_account: "blue",
    account: "brown"
};

const component = {
    group_header: "Group Headers",
    main_group: "Main Groups",
    sub_account: "Sub Accounts",
    account: "Accounts"
}

export default function CoaTreeView() {
    const spinService = SpinService();
    const [expanded, setExpanded] = React.useState([]);
    const [open, setOpen] = React.useState(false);
    const [masterId, setMasterId] = React.useState(undefined);
    const [config, setConfig] = React.useState(null);
    const [treeViewData, setTreeViewData] = React.useState([]);

    const getMasters = async () => {
        spinService.on();
        const response = await get(EntityConstants.ACCOUNT_TREE);
        const data = await response.json();
        setTreeViewData(data);
        spinService.off();
    };

    const handleChange = (code) => {
        if (expanded.indexOf(code) > -1) {
            setExpanded((prevList) => prevList.filter((value) => value !== code));
        } else {
            setExpanded((prevList) => [...prevList, code]);
        }
    };

    const handleExpandAll = () => {
        if (expanded.length === 0) {
            const allNodeIds = getAllNodeIds(treeViewData);
            setExpanded(allNodeIds);
        } else {
            setExpanded([]);
        }
    };

    const getAllNodeIds = (items) => {
        let nodeIds = items.map((item) => item.code);
        if (Array.isArray(items)) {
            items.forEach((node) => {
                nodeIds = nodeIds.concat(getAllNodeIds(node.main_groups !== undefined ? node.main_groups : node.sub_accounts !== undefined ? node.sub_accounts : node.accounts !== undefined ? node.accounts : []));
            });
        }
        return nodeIds;
    };

    const handleViewItem = (item) => {
        setConfig(Config.find((config) => config.componentName === component[item.type]));
        setMasterId(item.id);
        setOpen(true);
    };

    const onClose = () => {
        setMasterId(undefined);
        setOpen(false);
        getMasters();
    };

    React.useEffect(() => {
        getMasters();
    }, []);


    const renderGroupHeaders = (group_headers) => {
        return <React.Fragment>
            {
                group_headers !== undefined && group_headers.length > 0 && group_headers.map((gh) => {
                    return <Accordion key={gh.code} expanded={expanded.indexOf(gh.code) > -1} >
                        <AccordionSummary
                            aria-controls="panel1d-content"
                            id="panel1d-header"
                            expandIcon={
                                gh.main_groups !== undefined && gh.main_groups.length === 0 ?
                                    <BlockRoundedIcon /> :
                                    gh.sub_accounts !== undefined && gh.sub_accounts.length === 0 ?
                                        <BlockRoundedIcon /> :
                                        gh.accounts !== undefined && gh.accounts.length === 0 ?
                                            <BlockRoundedIcon /> :
                                            gh.main_groups === undefined && gh.sub_accounts === undefined && gh.accounts === undefined ?
                                                <BlockRoundedIcon /> :
                                                expanded.indexOf(gh.code) === -1 ?
                                                    <AddCircleOutlineRoundedIcon onClick={() => handleChange(gh.code)} /> :
                                                    <HighlightOffRoundedIcon onClick={() => handleChange(gh.code)} />
                            }
                        >
                            <Typography color={colors[gh.type]} onClick={() => handleViewItem(gh)}> <b>{gh.code}:</b>{gh.name}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {gh.main_groups !== undefined && renderGroupHeaders(gh.main_groups)}
                            {gh.sub_accounts !== undefined && renderGroupHeaders(gh.sub_accounts)}
                            {gh.accounts !== undefined && renderGroupHeaders(gh.accounts)}
                        </AccordionDetails>
                    </Accordion>
                })
            }
        </React.Fragment>
    };

    return (
        <React.Fragment>
            <Box sx={{ margin: '2%' }}>
                <Box sx={{ mb: 1 }}>
                    <Button variant="contained" color="info" onClick={handleExpandAll}>
                        {expanded.length === 0 ? 'Expand all' : 'Collapse all'}
                    </Button>
                </Box>
                {renderGroupHeaders(treeViewData)}
            </Box>

            {
                config !== null && open && <GoDrawer
                    title={config.title}
                    open={open}
                    onClose={() => onClose()}
                >
                    <ConfigService name={config.title} onClose={() => onClose()} id={masterId} />
                </GoDrawer>
            }
        </React.Fragment>
    );
}